import config from '../../../config';
import AmplitudeUtils from '../../../utils/amplitude-utils';
import BaseMfaVerifyOieCustomizer from './base';
import React from 'react';
import Footer from '../../../components/mfa-verify/Footer';
import { isAnetUser } from '../../../utils/user-utils';

const { oieEnabled } = config;

/**
 * Customizer for email OTP verification during password reset flow
 * Applies Forge styling to Okta's native email OTP screen
 *
 * Controller: mfa-verify-passcode
 * Authenticator: okta_email
 * Method: email
 *
 * This follows the same pattern as MfaVerifyPasscodePhoneOieCustomizer
 * but for email OTP instead of SMS/Voice Call.
 */
export default class MfaVerifyPasscodeEmailCustomizer extends BaseMfaVerifyOieCustomizer {
  primaryButtonClassName = 'fe_u_margin--bottom-small';
  headerText = 'Email Verification';
  alwaysDisplayBackToLogin = true;

  get footerText() {
    return 'Please contact your administrator if you need help setting up or managing authentication methods.';
  }

  protected getFooter({ footerLink }: { footerLink: JQuery }): React.JSX.Element {
    // Hide "Reset your account" link during password reset flow
    return (
      <Footer
        text={this.footerText}
        footerLinks={footerLink}
        alwaysDisplayBackToSignIn={this.alwaysDisplayBackToLogin}
        displayResetAccountLink={false}
      />
    );
  }

  primaryButtonOnClick = () => {
    AmplitudeUtils.logCustomEvent('passwordResetEmailOtpVerifyClicked', {
      username: this.context.identifier,
    });
  };

  onInfoContainerLinkClick = () => {
    AmplitudeUtils.logCustomEvent('passwordResetEmailOtpResendClicked', {
      username: this.context.identifier,
    });
  };

  async shouldCustomize() {
    return (
      this.context?.controller === 'mfa-verify-passcode' &&
      this.context?.authenticatorKey === 'okta_email' &&
      isAnetUser() &&
      oieEnabled
    );
  }

  async customize() {
    // Set headerDescription dynamically before calling super.customize()
    this.headerDescription = 'Your new email has been sent, if you don\'t receive it, please wait 60 seconds and try again.';

    await super.customize();

    AmplitudeUtils.logCustomEvent('passwordResetEmailOtpScreenLoaded', {
      username: this.context.identifier,
    });
  }

  protected forgifyController() {
    // For email MFA, Okta puts the resend link in o-form-error-container instead of o-form-info-container
    // Rename it so our InfoContainer component can handle it properly
    const errorContainer = jQueryCourage('.o-form-error-container');
    if (errorContainer.length) {
      errorContainer.removeClass('o-form-error-container');
      errorContainer.addClass('o-form-info-container');
    }
    // Now call parent's forgifyController - it will treat the error container as an info container
    super.forgifyController();
  }
}
